import { Component, OnInit, Inject, ViewChild, ElementRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Clipboard } from '@angular/cdk/clipboard';
import { AsyncJob } from '../../models/async-job.model';
import { AsyncJobService } from '../../services/async-job.service';
import { ImportAsyncJobDialogBase } from '../async-job-base/import-async-job-base.dialog';
import { GenericImportDataFromExcelSpreadsheet, ImportDataServiceNames } from '../../models/export-import/generic-import-data-from-excel-spreadsheet';
import { ExcelImportService, ImportDataFromExcelDialogAction } from '../../services/excel-import.service';
import { FileInputUtil } from '../../utility-classes/file-input.util';
import { ExcelImportMetrics } from '../../models/export-import/excel-import-metrics.model';

export class ImportDataFromExcelDialogConfig {
    public action: ImportDataFromExcelDialogAction;

    // Note:  only one or possibly two of the following Id values will be set.
    public dataCollectionId: number;
    public formInstanceId: number;
    public exportOnly: boolean = false;
}

@Component({
    selector: 'app-import-data',
    templateUrl: './import-data-from-excel.dialog.html',
    styleUrls: [
        '../async-job-base/async-job-base.dialog.scss',
        './import-data-from-excel.dialog.scss'
    ],
    standalone: false
})
export class ImportDataFromExcelDialog extends ImportAsyncJobDialogBase implements OnInit {
    // Properties.
    // File-related properties.
    @ViewChild('file') file;
    @ViewChild('executionSummary') executionSummary: ElementRef<HTMLElement>;
    //protected fileSet: Set<File> = new Set();
    private files: File[] = [];
    private selectedFileName: string = null;
    public validationMessages: string = "";
    private excelImportMetrics: ExcelImportMetrics = null;

    // Constructor.
    public constructor(public dialogRef: MatDialogRef<ImportDataFromExcelDialog>,
        @Inject(MAT_DIALOG_DATA) public config: ImportDataFromExcelDialogConfig,
        private excelImportService: ExcelImportService,
        protected injectedAsyncJobService: AsyncJobService,
        private clipboard: Clipboard) {
        super(injectedAsyncJobService)
    }

    // Implement a base class's abstract method.
    public get ComponentClassName(): string {
        return 'ImportDataFromExcelDialog';
    }

    // Life cycle methods.
    public ngOnInit(): void {
    }

    // Accessor methods.
    public get Title(): string {
        let title: string = null;

        if (this.config.action == ImportDataFromExcelDialogAction.ImportSiteStructure)
            title = 'Import Site Structure';
        else if (this.config.action == ImportDataFromExcelDialogAction.ImportSiteFormInstances)
            title = 'Import Form Instances';
        else if (this.config.action == ImportDataFromExcelDialogAction.ImportFormInstanceGrids)
            title = 'Import Form Grid Data';
        else if (this.config.action == ImportDataFromExcelDialogAction.ImportValuesToExistingFormInstances)
            title = 'Import Values to Existing Form Instances';

        return title;
    }

    public get ImportDescription(): string {
        let description: string = null;

        if (this.config.action == ImportDataFromExcelDialogAction.ImportSiteStructure)
            description = 'Import spreadsheet as site structure';
        else if (this.config.action == ImportDataFromExcelDialogAction.ImportSiteFormInstances)
            description = 'Import spreadsheet containing form instances for the entire Site';
        else if (this.config.action == ImportDataFromExcelDialogAction.ImportFormInstanceGrids)
            description = 'Import data from multiple form grids';
        else if (this.config.action == ImportDataFromExcelDialogAction.ImportValuesToExistingFormInstances)
            description = 'Import values to specific fields of existing form instances';

        return description;
    }

    public CopySummaryToClipboard() {
        this.clipboard.copy(this.ExecutionSummaryText);
    }

    public get AllowMultipleFiles(): boolean {
        // Note:  importing from multiple files is presently not a requirement.
        return false;
    }

    public get FileSelected(): boolean {
        return this.files.length > 0;
    }
    public get SelectedFileName(): string {
        let selectedFileName: string = 'No file selected';

        if (this.files.length == 1) {
            selectedFileName = this.selectedFileName;
        }

        return selectedFileName;
    }

    // Handle dialogue action(s).
    public selectImportFile(): void {
        FileInputUtil.clearFileSelection(this.file);
        this.file.nativeElement.click(); // Cause the hidden file control to open.
    }

    public onFileAdded(): void {
        // Clear any previous file.
        this.selectedFileName = null;

        // Save the selected file.
        const files: { [key: string]: File } = this.file.nativeElement.files;

        for (let key in files) {
            if (!isNaN(parseInt(key))) {
                let selectedFile: File = files[key];

                //this.fileSet.add(selectedFile);
                this.files.push(selectedFile);

                this.selectedFileName = selectedFile.name; // We will only save the first file's name for now.
            }
        }
    }

    public startImport(): void {
        let importInstructions: GenericImportDataFromExcelSpreadsheet = new GenericImportDataFromExcelSpreadsheet();

        // Seems like this could be streamlined - I don't think we need the if/else ifs (Does the action name and the service need to be different?)
        if (this.config.action == ImportDataFromExcelDialogAction.ImportSiteStructure) {
            importInstructions.serviceName = ImportDataServiceNames.ImportSiteStructureService;
            importInstructions.dataCollectionId = this.config.dataCollectionId;

            this.excelImportService.genericImportDataFromExcelSpreadsheet(importInstructions, this.files).then(asyncJob => {
                this.startMonitoringJob(asyncJob, this.jobCompletedArrowFunctionImport, this.jobUpdatedArrowFunctionImport);
            });
        }
        else if (this.config.action == ImportDataFromExcelDialogAction.ImportSiteFormInstances) {
            importInstructions.serviceName = ImportDataServiceNames.ImportSiteFormInstancesService;
            importInstructions.dataCollectionId = this.config.dataCollectionId;

            this.excelImportService.genericImportDataFromExcelSpreadsheet(importInstructions, this.files).then(asyncJob => {
                this.startMonitoringJob(asyncJob, this.jobCompletedArrowFunctionImport, this.jobUpdatedArrowFunctionImport);
            });
        }
        else if (this.config.action == ImportDataFromExcelDialogAction.ImportFormInstanceGrids) {
            importInstructions.serviceName = ImportDataServiceNames.ImportFormInstanceGridsService;
            importInstructions.dataCollectionId = this.config.dataCollectionId;

            this.excelImportService.genericImportDataFromExcelSpreadsheet(importInstructions, this.files).then(asyncJob => {
                this.startMonitoringJob(asyncJob, this.jobCompletedArrowFunctionImport, this.jobUpdatedArrowFunctionImport);
            });
        }
        else if (this.config.action == ImportDataFromExcelDialogAction.ImportValuesToExistingFormInstances) {
            importInstructions.serviceName = ImportDataServiceNames.ImportValuesToExistingFormInstancesService;
            importInstructions.dataCollectionId = this.config.dataCollectionId;

            this.excelImportService.genericImportDataFromExcelSpreadsheet(importInstructions, this.files).then(asyncJob => {
                this.startMonitoringJob(asyncJob, this.jobCompletedArrowFunctionImport, this.jobUpdatedArrowFunctionImport);
            });
        }
    }

    public get ExecutionSummaryText(): string {
        let summaryText: string = "";
        if (this.validationMessages) {
            return this.validationMessages;
        }
        summaryText = super.deriveExecutionSummaryText(this.excelImportMetrics);
        // scroll down as messages get added
        if (summaryText && this.executionSummary) {
            this.executionSummary.nativeElement.scrollTop = this.executionSummary.nativeElement.scrollHeight;
        }
        return (summaryText);
    }

    public SplitPipe(theString): string {
        if (theString) {
            return theString.split("|");
        }
    }
    private jobUpdatedArrowFunctionImport = (asyncJob: AsyncJob): void => {
        if (asyncJob.stdOut) {
            this.excelImportMetrics = this.parseImportMetrics(asyncJob.stdOut);
        }
        this.resetIdleTimer();
        return;
    }

    private parseImportMetrics(jobMetricsJson: string): ExcelImportMetrics {
        let jobMetricsObject: Object = JSON.parse(jobMetricsJson);
        let metrics: ExcelImportMetrics = new ExcelImportMetrics(jobMetricsObject);
        return (metrics);
    }

    private jobCompletedArrowFunctionImport = (asyncJob: AsyncJob): void => {
        if (asyncJob.stdOut) {
            this.validationMessages = asyncJob.stdOut;
        }
    }

    public dismiss(): void {
        this.dialogRef.close(null);
    }
    public cancel(): void {
        this.dialogRef.close(null);
    }
}
